export function EnforcementPolicies() {
  const violations = [
    {
      violation: 'Operating without business license',
      firstOffense: '$500 - $1,000',
      repeat: '$2,000 - $5,000',
      consequences: 'License application denial, platform delisting'
    },
    {
      violation: 'Zoning bylaw violations',
      firstOffense: '$1,000 - $2,000',
      repeat: '$3,000 - $10,000',
      consequences: 'Cease operations order, legal action'
    },
    {
      violation: 'Fire safety non-compliance',
      firstOffense: '$1,500 - $3,000',
      repeat: '$5,000 - $15,000',
      consequences: 'Immediate closure, criminal charges possible'
    },
    {
      violation: 'Noise or nuisance complaints',
      firstOffense: '$250 - $500',
      repeat: '$1,000 - $2,500',
      consequences: 'License suspension or revocation'
    },
    {
      violation: 'Tax non-compliance (MRDT/PST)',
      firstOffense: '$500 + back taxes',
      repeat: '$2,000 + penalties',
      consequences: 'Provincial enforcement, interest charges'
    },
    {
      violation: 'Exceeding occupancy limits',
      firstOffense: '$500 - $1,000',
      repeat: '$2,000 - $5,000',
      consequences: 'License restrictions, guest eviction'
    }
  ];

  const complianceTips = [
    {
      icon: 'ri-shield-check-line',
      title: 'Maintain Valid License',
      description: 'Keep business license current and display license number prominently in all listings and property.'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Document Everything',
      description: 'Maintain records of safety inspections, insurance, tax payments, and guest registrations for minimum 3 years.'
    },
    {
      icon: 'ri-team-line',
      title: 'Good Neighbor Relations',
      description: 'Provide neighbors with contact information, address concerns promptly, and enforce quiet hours strictly.'
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Safety First',
      description: 'Regular testing of smoke/CO detectors, clear emergency procedures, and proper fire extinguisher maintenance.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Tax Compliance',
      description: 'Collect and remit MRDT and PST correctly, maintain detailed financial records, and file all required returns.'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Stay Updated',
      description: 'Monitor City of Surrey announcements for regulatory changes and adapt operations to new requirements promptly.'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            2026 Enforcement
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Enforcement & Penalties
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Surrey is actively monitoring STR compliance. Understanding enforcement policies and maintaining compliance protects your investment and reputation.
          </p>
        </div>

        {/* Penalty Structure */}
        <div className="mb-20">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-10 text-center">
            Violation Penalty Structure
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl overflow-hidden shadow-md">
              <thead className="bg-[#882629] text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Violation Type</th>
                  <th className="px-6 py-4 text-left font-semibold">First Offense</th>
                  <th className="px-6 py-4 text-left font-semibold">Repeat Offense</th>
                  <th className="px-6 py-4 text-left font-semibold">Additional Consequences</th>
                </tr>
              </thead>
              <tbody>
                {violations.map((item, index) => (
                  <tr 
                    key={index}
                    className={index % 2 === 0 ? 'bg-[#F0F0F0]' : 'bg-white'}
                  >
                    <td className="px-6 py-4 font-medium text-[#505A5A]">{item.violation}</td>
                    <td className="px-6 py-4 text-[#882629] font-semibold whitespace-nowrap">{item.firstOffense}</td>
                    <td className="px-6 py-4 text-[#882629] font-semibold whitespace-nowrap">{item.repeat}</td>
                    <td className="px-6 py-4 text-[#505A5A]/70 text-sm">{item.consequences}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Compliance Best Practices */}
        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-10 text-center">
            Compliance Best Practices
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceTips.map((tip, index) => (
              <div 
                key={index}
                className="bg-[#F0F0F0] rounded-2xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#882629] rounded-xl mb-4">
                  <i className={`${tip.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {tip.title}
                </h4>
                <p className="text-sm text-[#505A5A]/70 leading-relaxed">
                  {tip.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Enforcement Process */}
        <div className="bg-[#F0F0F0] rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-6 flex items-center">
            <i className="ri-police-badge-line text-[#882629] text-3xl mr-3"></i>
            How Surrey Enforces STR Regulations
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-[#505A5A] mb-3">Complaint-Based Enforcement</h4>
              <ul className="space-y-2 text-[#505A5A]/70 text-sm">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Neighbor complaints trigger investigations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Bylaw officers conduct site inspections</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Warning letters issued for first-time violations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Escalation to fines for non-compliance</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#505A5A] mb-3">Proactive Monitoring</h4>
              <ul className="space-y-2 text-[#505A5A]/70 text-sm">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Platform listing audits for unlicensed operators</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Cross-referencing with business license database</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Random safety inspections for licensed properties</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-[#882629] mt-0.5 mr-2"></i>
                  <span>Coordination with provincial tax authorities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-[#882629] rounded-2xl p-8 text-white text-center">
          <i className="ri-error-warning-line text-5xl mb-4"></i>
          <h3 className="text-2xl font-bold mb-4">Enforcement Style: Complaint-Based + Proactive Monitoring</h3>
          <p className="text-lg mb-6 max-w-3xl mx-auto opacity-90">
            Surrey combines complaint-based investigations with proactive platform monitoring. The city is increasing enforcement as the STR market grows. Operating without proper licensing and compliance can result in significant fines, legal action, and permanent damage to your rental business reputation.
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-white text-[#882629] px-10 py-4 rounded-full text-lg font-medium hover:bg-[#F0F0F0] transition-all cursor-pointer whitespace-nowrap shadow-xl"
          >
            Ensure Your Compliance
          </button>
        </div>
      </div>
    </section>
  );
}
